// Centralized configuration service for API base URLs, feature flags, and runtime env

import { useAuthStore } from "@store";

type SmartieData = {
  backendApiBaseUrl?: string;
  featureFlags?: Record<string, unknown>;
  sseDefaults?: Record<string, unknown>;
};

type SmartieWindow = Window & { smartieAiData?: SmartieData };

type ImportMetaEnvLike = { env?: Record<string, unknown> };

// Local override for tests/dev tooling
let backendRootOverride: string | null = null;

function coerceBoolean(value: unknown, defaultValue: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const lower = value.trim().toLowerCase();
    if (lower === "true" || lower === "1") return true;
    if (lower === "false" || lower === "0") return false;
  }
  return defaultValue;
}

function normalizeBackendRoot(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  if (!trimmed) return "";
  // Remove trailing slash
  let url = trimmed.replace(/\/$/, "");
  // Strip trailing /api, /api/v1 or /api/v2 if present
  const apiVersionMatch = url.match(/^(.*)\/api\/(v1|v2)\/?$/);
  if (apiVersionMatch) {
    url = apiVersionMatch[1];
  } else {
    const apiOnlyMatch = url.match(/^(.*)\/api\/?$/);
    if (apiOnlyMatch) {
      url = apiOnlyMatch[1];
    }
  }
  // Final trim of trailing slash if regex left one
  return url.replace(/\/$/, "");
}

function readFromAuthStore(): string | undefined {
  try {
    const state = useAuthStore.getState();
    return state.apiUrls?.backendBaseUrl || undefined;
  } catch {
    return undefined;
  }
}

function readFromWindow(): string | undefined {
  try {
    const w = window as unknown as SmartieWindow;
    return w.smartieAiData?.backendApiBaseUrl || undefined;
  } catch {
    return undefined;
  }
}

function readFromEnv(): string | undefined {
  try {
    const val = (import.meta as unknown as ImportMetaEnvLike).env?.[
      "VITE_SMARTIE_BACKEND_ROOT"
    ];
    return typeof val === "string" ? val : undefined;
  } catch {
    return undefined;
  }
}

export function getBackendRoot(): string {
  if (backendRootOverride) {
    return normalizeBackendRoot(backendRootOverride);
  }
  const fromStore = readFromAuthStore();
  const fromWindow = readFromWindow();
  const fromEnv = readFromEnv();

  const selected =
    fromStore || fromWindow || fromEnv || "http://127.0.0.1:8000";
  return normalizeBackendRoot(selected);
}

export function getApiBaseUrl(version: "v1" | "v2"): string {
  const root = getBackendRoot();
  return `${root}/api/${version}`;
}

export function setBackendRootOverride(url: string | null): void {
  backendRootOverride = url;
}

export type FeatureFlags = {
  sseBackoffEnabled?: boolean;
  debugLogsEnabled?: boolean;
  // Extend with more flags as needed
};

export function getFeatureFlag<T extends keyof FeatureFlags>(
  flag: T,
  defaultValue: NonNullable<FeatureFlags[T]> extends boolean
    ? boolean
    : FeatureFlags[T],
): FeatureFlags[T] {
  const w =
    typeof window !== "undefined"
      ? (window as unknown as SmartieWindow)
      : undefined;
  const wpValue = w?.smartieAiData?.featureFlags?.[flag as string];

  // Try env as fallback for simple boolean flags with naming convention VITE_FEATURE_<FLAG>
  const envKey = `VITE_FEATURE_${String(flag).toUpperCase()}`;
  const envValue = (import.meta as unknown as ImportMetaEnvLike)?.env?.[envKey];

  const chosen =
    wpValue !== undefined
      ? wpValue
      : envValue !== undefined
        ? envValue
        : defaultValue;

  // Coerce boolean-like values for common flags
  if (typeof defaultValue === "boolean") {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - we know return type should be boolean here
    return coerceBoolean(chosen, defaultValue) as FeatureFlags[T];
  }

  return chosen as FeatureFlags[T];
}

export function getRuntimeEnv(): {
  isDev: boolean;
  isProd: boolean;
  isWordPress: boolean;
  isE2E: boolean;
} {
  const isDev = !!(import.meta as unknown as ImportMetaEnvLike).env?.["DEV"];
  const isProd = !!(import.meta as unknown as ImportMetaEnvLike).env?.["PROD"];
  const isWordPress =
    typeof window !== "undefined" &&
    !!(window as unknown as SmartieWindow)?.smartieAiData;
  const isE2E = !!(import.meta as unknown as ImportMetaEnvLike).env?.[
    "VITE_E2E"
  ];
  return { isDev, isProd, isWordPress, isE2E };
}

// SSE defaults with ability to override from window.smartieAiData or env
export function getSseDefaults(): {
  retryBaseMs: number;
  retryFactor: number;
  retryMaxMs: number;
  retryMaxAttempts: number;
  stallTimeoutMs: number;
} {
  const w =
    typeof window !== "undefined"
      ? (window as unknown as SmartieWindow)
      : undefined;
  const cfg = (w?.smartieAiData?.sseDefaults as Record<string, unknown>) || {};
  const env = (import.meta as unknown as ImportMetaEnvLike).env || {};

  const parseNum = (v: unknown, def: number): number => {
    if (typeof v === "number" && !Number.isNaN(v)) return v;
    if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v)))
      return Number(v);
    return def;
  };

  const retryBaseMs = parseNum(
    cfg.retryBaseMs ?? env.VITE_SSE_RETRY_BASE_MS,
    1000,
  );
  const retryFactor = parseNum(cfg.retryFactor ?? env.VITE_SSE_RETRY_FACTOR, 2);
  const retryMaxMs = parseNum(
    cfg.retryMaxMs ?? env.VITE_SSE_RETRY_MAX_MS,
    5000,
  );
  const retryMaxAttempts = parseNum(
    cfg.retryMaxAttempts ?? env.VITE_SSE_RETRY_MAX_ATTEMPTS,
    6,
  );
  const stallTimeoutMs = parseNum(
    cfg.stallTimeoutMs ?? env.VITE_SSE_STALL_TIMEOUT_MS,
    60000, // Increased from 15s to 60s to handle slower models like gemini-3-pro-preview
  );

  return {
    retryBaseMs,
    retryFactor,
    retryMaxMs,
    retryMaxAttempts,
    stallTimeoutMs,
  };
}
